import type { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { adminAuth } from '../lib/adminApi';
import { AdminSearchBar } from './AdminSearchBar';
import { Logo } from './Logo';
import { SEO } from './SEO';

/**
 * Admin shell with a left sidebar (Acting-Office style). All admin pages
 * render their content inside <main>; the sidebar stays fixed, scroll
 * happens only in the main column.
 *
 * On phones the sidebar collapses to a top bar with the nav items as
 * horizontal chips so the chrome stays usable.
 */
export function AdminLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  function logout() {
    adminAuth.logout();
    navigate('/admin/login');
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Admin — Dormant Accounts Filing" noindex />
      {/* ─── Mobile top bar ────────────────────────────────────────── */}
      <header className="lg:hidden border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/admin" aria-label="Admin home" className="flex items-center gap-2">
            <Logo variant="mark" />
            <span className="text-sm font-semibold text-slate-900">Admin</span>
          </Link>
          <button onClick={logout} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">
            Log out
          </button>
        </div>
        <div className="px-4 pb-3">
          <AdminSearchBar />
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-slate-100 px-3 py-2 text-xs">
          <MobileNav to="/admin">Dashboard</MobileNav>
          <MobileNav to="/admin/customers">Customers</MobileNav>
          <MobileNav to="/admin/filings">Filings</MobileNav>
          <MobileNav to="/admin/upcoming">Upcoming</MobileNav>
          <MobileNav to="/admin/filings?archive=archived">Archived</MobileNav>
          <MobileNav to="/admin/file-now">File now</MobileNav>
          <MobileNav to="/admin/tasks">Tasks</MobileNav>
          <MobileNav to="/admin/messages">Messages</MobileNav>
          <MobileNav to="/admin/leads">Leads</MobileNav>
          <MobileNav to="/admin/blog">Blog</MobileNav>
          <MobileNav to="/admin/settings">Settings</MobileNav>
        </nav>
      </header>

      <div className="lg:flex">
        {/* ─── Desktop sidebar ──────────────────────────────────────── */}
        <aside className="hidden lg:flex lg:w-60 lg:flex-shrink-0 lg:flex-col lg:fixed lg:inset-y-0 lg:border-r lg:border-slate-200 lg:bg-white">
          <div className="flex items-center gap-2.5 border-b border-slate-200 px-5 py-4">
            <Logo variant="mark" />
            <div>
              <div className="text-sm font-semibold text-brand-700 leading-tight">
                Dormant Filing
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                Admin
              </div>
            </div>
          </div>

          <nav className="flex-1 space-y-0.5 overflow-y-auto p-3 text-sm">
            <SidebarLink to="/admin" end icon={<IconDashboard />}>Dashboard</SidebarLink>

            <SidebarSection>Operations</SidebarSection>
            <SidebarLink to="/admin/customers" icon={<IconUsers />}>Customers</SidebarLink>
            <SidebarLink to="/admin/filings" icon={<IconDoc />}>Filings</SidebarLink>
            <SidebarLink to="/admin/upcoming" icon={<IconCalendar />}>Upcoming</SidebarLink>
            <SidebarLink to="/admin/filings?archive=archived" icon={<IconArchive />}>Archived</SidebarLink>
            <SidebarLink to="/admin/file-now" icon={<IconBolt />}>File now</SidebarLink>
            <SidebarLink to="/admin/tasks" icon={<IconCheck />}>Tasks</SidebarLink>
            <SidebarLink to="/admin/messages" icon={<IconMail />}>Messages</SidebarLink>

            <SidebarSection>Sales</SidebarSection>
            <SidebarLink to="/admin/leads" icon={<IconFlame />}>Leads</SidebarLink>

            <SidebarSection>Content</SidebarSection>
            <SidebarLink to="/admin/blog" icon={<IconBook />}>Blog</SidebarLink>

            <SidebarSection>System</SidebarSection>
            <SidebarLink to="/admin/settings" icon={<IconSettings />}>Settings</SidebarLink>
            <a
              href="/"
              className="flex items-center gap-2.5 rounded-md px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
            >
              <IconExternal />
              View site
            </a>
          </nav>

          <div className="border-t border-slate-200 p-3">
            <button
              onClick={logout}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-900"
            >
              <IconLogout />
              Log out
            </button>
          </div>
        </aside>

        {/* ─── Main column ─────────────────────────────────────────── */}
        <main className="flex-1 lg:pl-60">
          {/* Sticky top bar (desktop only) — universal search lives here. */}
          <div className="sticky top-0 z-20 hidden border-b border-slate-200 bg-white/95 backdrop-blur lg:block">
            <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
              <AdminSearchBar />
            </div>
          </div>
          <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Tiny header to break sidebar items into visual groups
 * (Operations / Sales / Content / System) — same idea as Debitam.
 */
function SidebarSection({ children }: { children: ReactNode }) {
  return (
    <div className="mt-5 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {children}
    </div>
  );
}

function SidebarLink({
  to,
  end,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-3 py-2 transition-colors ${
          isActive
            ? 'bg-brand-50 text-brand-700 font-medium'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
    >
      <span className="text-current">{icon}</span>
      {children}
    </NavLink>
  );
}

function MobileNav({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/admin'}
      className={({ isActive }) =>
        `whitespace-nowrap rounded-full px-3 py-1.5 font-medium ${
          isActive ? 'bg-brand-100 text-brand-700' : 'text-slate-600'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

// ─── Inline icons ─────────────────────────────────────────────────────────
function IconDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconDoc() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
function IconMail() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </svg>
  );
}
function IconExternal() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
function IconBook() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function IconBolt() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function IconFlame() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}
function IconCalendar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconArchive() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="3" width="20" height="5" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}
function IconLogout() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
